import type { RedoAdminRpcClient } from "@redotech/admin-sdk/rpc/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import EyeIcon from "@redotech/redo-icons/eye.svg";
import PlayIcon from "@redotech/redo-icons/play.svg";
import StopIcon from "@redotech/redo-icons/stop-circle.svg";
import { toast } from "@redotech/redo-web/alert";
import {
  RedoBadge,
  type RedoBadgeProps,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  memo,
  useMemo,
  useState,
  type JSXElementConstructor,
  type ReactNode,
} from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import {
  TRUEUP_JOB_SUMMARY,
  TRUEUP_OPTIONS_BY_KEY,
} from "./support-v2-maintenance-options";

type TrueupStatus = Awaited<
  ReturnType<RedoAdminRpcClient["getSupportV2TrueupResult"]>
>["status"];

type TrueupResult = NonNullable<
  Awaited<ReturnType<RedoAdminRpcClient["getSupportV2TrueupResult"]>>["result"]
>;

const RESULT_POLL_INTERVAL_MS = 3000;
const numberFormatter = new Intl.NumberFormat();
const formatCount = (value: number) => numberFormatter.format(value);

const formatError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const STATUS_BADGE: Record<
  TrueupStatus,
  {
    color: RedoBadgeProps["color"];
    Icon?: JSXElementConstructor<unknown>;
    text: string;
  }
> = {
  cancelled: { color: "gray", Icon: StopIcon, text: "Cancelled" },
  completed: { color: "success", Icon: CheckCircleIcon, text: "Completed" },
  failed: { color: "error", Icon: AlertTriangleIcon, text: "Failed" },
  running: { color: "blue", Icon: ClockIcon, text: "Running" },
  unknown: { color: "gray", text: "Unknown" },
};

const StatItem = ({ label, value }: { label: string; value: number }) => (
  <Flex align="center" gap="md" justify="space-between" w="full">
    <Text fontSize="sm" textColor="secondary">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium">
      {formatCount(value)}
    </Text>
  </Flex>
);

const StatGroup = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Flex dir="column" gap="xs">
    <Text fontSize="xs" fontWeight="medium" textColor="secondary">
      {title}
    </Text>
    {children}
  </Flex>
);

const TrueupResultView = memo(function TrueupResultView({
  result,
}: {
  result: TrueupResult;
}) {
  return (
    <Flex dir="column" gap="md">
      <RedoBadge
        color={result.dryRun ? "gray" : "brand"}
        size="sm"
        text={result.dryRun ? "Preview plan" : "Applied"}
      />
      <StatGroup title="Scan">
        <StatItem label="Open conversations" value={result.openConvs} />
        <StatItem label="Absent in v2" value={result.absent} />
        <StatItem label="Needs legacy stamp" value={result.needsLegacyStamp} />
      </StatGroup>
      <StatGroup title="Matched">
        <StatItem label="By legacy id" value={result.matched.legacy} />
        <StatItem label="By provider thread" value={result.matched.provider} />
        <StatItem label="By content" value={result.matched.content} />
        <StatItem label="By voice" value={result.matched.voice} />
      </StatGroup>
      {!result.dryRun && (
        <>
          <StatGroup title="Writes">
            <StatItem
              label="Status updated"
              value={result.writes.statusUpdated}
            />
            <StatItem
              label="Assignee updated"
              value={result.writes.assigneeUpdated}
            />
            <StatItem label="Read updated" value={result.writes.readUpdated} />
            <StatItem
              label="Tags reconciled"
              value={result.writes.tagsReconciled}
            />
            <StatItem
              label="Legacy stamped"
              value={result.writes.legacyStamped}
            />
          </StatGroup>
          <StatGroup title="Migration">
            <StatItem label="Inserted" value={result.migration.inserted} />
            <StatItem label="Updated" value={result.migration.updated} />
            <StatItem label="Repaired" value={result.migration.repaired} />
            <StatItem label="Skipped" value={result.migration.skipped} />
            <StatItem label="Failed" value={result.migration.failed} />
          </StatGroup>
        </>
      )}
    </Flex>
  );
});

export const TrueUpSupportV2Modal = memo(function TrueUpSupportV2Modal({
  teamId,
  teamLabel,
  onClose,
}: {
  teamId: string;
  teamLabel?: string;
  onClose: () => void;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [matchVoice, setMatchVoice] = useState(false);
  const [noMigrateMissing, setNoMigrateMissing] = useState(false);
  const [limitText, setLimitText] = useState("");
  const [confirmApply, setConfirmApply] = useState(false);
  const [activeRun, setActiveRun] = useState<{
    workflowId: string;
    dryRun: boolean;
  } | null>(null);

  const { limitValue, limitError } = useMemo(() => {
    const trimmed = limitText.trim();
    if (trimmed.length === 0) {
      return { limitValue: undefined, limitError: undefined };
    }
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      return {
        limitValue: undefined,
        limitError: "Enter a positive whole number, or leave blank.",
      };
    }
    return { limitValue: parsed, limitError: undefined };
  }, [limitText]);

  const runMutation = useMutation({
    mutationFn: (dryRun: boolean) =>
      rpcClient.runSupportV2Trueup({
        teamId,
        dryRun,
        ...(matchVoice ? { matchVoice: true } : {}),
        ...(noMigrateMissing ? { noMigrateMissing: true } : {}),
        ...(limitValue !== undefined ? { limit: limitValue } : {}),
      }),
    onSuccess: (result, dryRun) => {
      setActiveRun({ workflowId: result.workflowId, dryRun });
      setConfirmApply(false);
    },
    onError: (error) => {
      toast(formatError(error), { variant: "error" });
    },
  });

  const resultQuery = useQuery({
    enabled: activeRun !== null,
    queryFn: () => {
      if (!activeRun) {
        throw new Error("No active true-up run");
      }
      return rpcClient.getSupportV2TrueupResult({
        workflowId: activeRun.workflowId,
      });
    },
    queryKey: ["supportV2TrueupResult", activeRun?.workflowId] as const,
    refetchInterval: (query) =>
      query.state.data?.status === "running" ? RESULT_POLL_INTERVAL_MS : false,
    refetchOnWindowFocus: false,
  });

  const runResult = resultQuery.data;
  const status: TrueupStatus | undefined = runResult?.status;
  const statusConfig = status ? STATUS_BADGE[status] : undefined;
  const runInFlight = runMutation.isPending || status === "running";

  const closeRequested = useHandler(() => {
    if (!runMutation.isPending) {
      onClose();
    }
  });

  const runPreview = useHandler(() => {
    runMutation.mutate(true);
  });

  const runApply = useHandler(() => {
    runMutation.mutate(false);
  });

  const primaryButton = confirmApply
    ? {
        disabled: limitError !== undefined || runInFlight,
        IconLeading: PlayIcon,
        onClick: runApply,
        pending: runMutation.isPending,
        text: "Apply changes",
        theme: "destructive" as const,
      }
    : {
        disabled: limitError !== undefined || runInFlight,
        IconLeading: EyeIcon,
        onClick: runPreview,
        pending: runMutation.isPending,
        text: "Run preview",
      };

  const secondaryButton = confirmApply
    ? {
        disabled: runMutation.isPending,
        onClick: () => setConfirmApply(false),
        text: "Back",
      }
    : {
        disabled: limitError !== undefined || runMutation.isPending,
        IconLeading: PlayIcon,
        onClick: () => setConfirmApply(true),
        text: "Apply…",
        theme: "destructive" as const,
      };

  return (
    <RedoModal
      headerIconAlignment="inline"
      modalSize="md"
      onModalCloseRequested={closeRequested}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      subtitle={
        teamLabel
          ? `Sync open v1 conversations into support-v2 for ${teamLabel}.`
          : "Sync open v1 conversations into support-v2."
      }
      theme={confirmApply ? "error" : "none"}
      title="True-up support-v2 tickets"
    >
      <Flex dir="column" gap="lg">
        <Text fontSize="sm" textColor="secondary">
          {TRUEUP_JOB_SUMMARY}
        </Text>

        {confirmApply && (
          <Text fontSize="sm" fontWeight="medium" textColor="error">
            Apply writes to support-v2: it can reopen/snooze tickets, set
            assignees and read state, reconcile tags, stamp legacy ids, and
            migrate missing conversations. Run a preview first if unsure.
          </Text>
        )}

        <RedoCollapseSubsection defaultCollapsed={false} title="Match options">
          <Flex dir="column" gap="md" pl="lg">
            <Flex dir="column" gap="xxs">
              <RedoCheckbox
                description={TRUEUP_OPTIONS_BY_KEY.matchVoice.description}
                label={TRUEUP_OPTIONS_BY_KEY.matchVoice.label}
                setValue={(value) => setMatchVoice(value === true)}
                size="sm"
                value={matchVoice}
              />
              {TRUEUP_OPTIONS_BY_KEY.matchVoice.riskNote && (
                <Text fontSize="xs" pl="3xl" textColor="warning">
                  {TRUEUP_OPTIONS_BY_KEY.matchVoice.riskNote}
                </Text>
              )}
            </Flex>
            <RedoCheckbox
              description={TRUEUP_OPTIONS_BY_KEY.noMigrateMissing.description}
              label={TRUEUP_OPTIONS_BY_KEY.noMigrateMissing.label}
              setValue={(value) => setNoMigrateMissing(value === true)}
              size="sm"
              value={noMigrateMissing}
            />
            <RedoTextInput
              description={TRUEUP_OPTIONS_BY_KEY.limit.description}
              errorText={limitError}
              inputTypeHint="number"
              label={TRUEUP_OPTIONS_BY_KEY.limit.label}
              min={1}
              setValue={setLimitText}
              size="sm"
              state={limitError ? "error" : "default"}
              value={limitText}
            />
          </Flex>
        </RedoCollapseSubsection>

        {runMutation.error && (
          <Text fontSize="sm" textColor="error">
            {formatError(runMutation.error)}
          </Text>
        )}

        {activeRun && (
          <>
            <Divider />
            <Flex dir="column" gap="md">
              <Flex align="center" gap="sm" justify="space-between" wrap="wrap">
                <Text fontSize="sm" fontWeight="medium">
                  {activeRun.dryRun ? "Preview run" : "Apply run"}
                </Text>
                {statusConfig && (
                  <RedoBadge
                    color={statusConfig.color}
                    segmentLeading={
                      statusConfig.Icon
                        ? { type: "icon", Icon: statusConfig.Icon, size: "sm" }
                        : undefined
                    }
                    size="sm"
                    text={statusConfig.text}
                  />
                )}
              </Flex>
              <Text
                fontFamily="monospace"
                fontSize="xs"
                overflow="hidden"
                textColor="secondary"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {activeRun.workflowId}
              </Text>

              {resultQuery.error && (
                <Text fontSize="sm" textColor="error">
                  {formatError(resultQuery.error)}
                </Text>
              )}
              {status === "running" && (
                <Text fontSize="sm" textColor="secondary">
                  Job running… results appear when it completes.
                </Text>
              )}
              {runResult?.error && (
                <Text fontSize="sm" textColor="error">
                  {runResult.error}
                </Text>
              )}
              {runResult?.result && (
                <TrueupResultView result={runResult.result} />
              )}
            </Flex>
          </>
        )}
      </Flex>
    </RedoModal>
  );
});
